import mustache from "mustache";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { fx_createFincaArbolesRequest } from "../../../../../modulos/configuracion/monitoreo/request/FincaArbolesRequest";
import { FincaArbolesSaveService } from "../../../../../modulos/configuracion/monitoreo/services/FincaArbolesSaveService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./arbolesMonitorearTemplate.html";

mustache.parse(template);

/** Cantidades ofrecidas como acceso rápido (mismos valores que el mockup). */
const PRESETS: number[] = [10, 15, 20, 25, 30, 50];

/** Espera tras la última tecla antes de guardar lo escrito en "Otra cantidad". */
const DEBOUNCE_MS: number = 600;

/** Título con el que se agrupan los avisos de esta sección en los toasts. */
const TOAST_TITULO: string = "Árboles a monitorear";

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let ARBOLES_ID: number = 0;

/** Sección "Árboles a monitorear" de la configuración de monitoreo: presets +
 *  cantidad libre, con **guardado automático** (sin botón guardar). El dato es
 *  de la finca (`arbolesMonitorear`), no del cultivo: hay un único valor por
 *  finca y un endpoint dedicado.
 *
 *  El feedback sale entero por los toasts del `errorService`; no hay línea de
 *  estado en la card. Tampoco hay aviso de "guardando": el valor se pinta al
 *  instante (optimista) y un toast vive 30s, mucho más de lo que dura el POST,
 *  así que quedaría colgado bastante después de haber terminado. */
export class ArbolesMonitorearRender implements IConnectRender {
  /** Id de instancia, para los ids del DOM. */
  private readonly _id: number;

  /** Servicio de guardado (`POST /configuracion/fincas-arboles/save`). */
  private readonly _saveService: FincaArbolesSaveService =
    new FincaArbolesSaveService();

  /** Valor mostrado; optimista, puede no estar confirmado todavía. */
  private _valor: number;

  /** Último valor confirmado por el server; a este se revierte si falla. */
  private _valorConfirmado: number;

  /** Si el último guardado terminó bien. Tras un fallo, reelegir el mismo valor
   *  vuelve a intentar en lugar de darlo por guardado. */
  private _ultimoGuardadoOk: boolean = true;

  constructor() {
    this._id = ++ARBOLES_ID;
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    this._valor = finca.arbolesMonitorear;
    this._valorConfirmado = finca.arbolesMonitorear;
  }

  /** HTML de la sección con el valor actual de la finca ya pintado. */
  render(): string {
    return mustache.render(template, {
      id: this._id,
      valor: this._valor,
      presets: PRESETS.map((valor) => ({
        valor: valor,
        cssClass: valor === this._valor ? "sx-btn-primary" : "sx-btn-ghost",
      })),
    });
  }

  /** Resuelve los elementos y engancha presets e input. */
  connect(): void {
    this._presets = document.getElementById(`mcArbolesPresets-${this._id}`);
    this._input = document.getElementById(
      `mcArbolesInput-${this._id}`,
    ) as HTMLInputElement | null;
    this._display = document.getElementById(`mcArbolesDisplay-${this._id}`);

    this._presets?.addEventListener("click", this._onPresetClick);
    this._input?.addEventListener("input", this._onInput);
    this._input?.addEventListener("blur", this._onInputCommit);
    this._input?.addEventListener("keydown", this._onInputKeyDown);
  }

  /** Cancela el debounce, invalida los guardados en vuelo y suelta listeners. */
  dispose(): void {
    this._disposed = true;
    this._cancelDebounce();
    // Cualquier respuesta que llegue después queda obsoleta contra este número.
    this._peticion++;

    this._presets?.removeEventListener("click", this._onPresetClick);
    this._input?.removeEventListener("input", this._onInput);
    this._input?.removeEventListener("blur", this._onInputCommit);
    this._input?.removeEventListener("keydown", this._onInputKeyDown);
  }

  /** Contenedor de los botones de preset (delegación de click). */
  private _presets: HTMLElement | null = null;
  /** Input de cantidad libre. */
  private _input: HTMLInputElement | null = null;
  /** Cifra de la stat tile. */
  private _display: HTMLElement | null = null;

  /** Marca de componente desmontado: nada debe tocar el DOM después. */
  private _disposed: boolean = false;

  /** Id del `setTimeout` del debounce del input, o null. */
  private _debounceId: number | null = null;

  /** Número de la última petición disparada: las respuestas que no coinciden
   *  llegaron fuera de orden (o después del dispose) y se descartan. */
  private _peticion: number = 0;

  /** Click en un preset: aplica ese valor y guarda de inmediato. */
  private _onPresetClick = (e: MouseEvent): void => {
    const boton = (e.target as HTMLElement).closest<HTMLButtonElement>(
      "button[data-valor]",
    );
    if (!boton) return;

    const valor = Number(boton.dataset.valor);
    if (!Number.isInteger(valor)) return;

    this._cancelDebounce();
    if (this._input) this._input.value = String(valor);
    this._aplicar(valor);
  };

  /** Tecleo en "Otra cantidad": reinicia el debounce. */
  private _onInput = (): void => {
    this._cancelDebounce();
    this._debounceId = window.setTimeout(() => {
      this._debounceId = null;
      this._commitInput();
    }, DEBOUNCE_MS);
  };

  /** Confirma lo escrito sin esperar el debounce (blur o Enter). */
  private _onInputCommit = (): void => {
    this._cancelDebounce();
    this._commitInput();
  };

  private _onInputKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") this._onInputCommit();
  };

  private _cancelDebounce(): void {
    if (this._debounceId === null) return;
    window.clearTimeout(this._debounceId);
    this._debounceId = null;
  }

  /** Valida lo escrito y, si sirve, lo aplica; si no, avisa y no guarda. */
  private _commitInput(): void {
    if (this._disposed) return;

    const valor = this._parseInput();
    if (valor === null) {
      serviceProvider
        .getErrorService()
        .addWarning(TOAST_TITULO, "Ingrese una cantidad entera mayor a 0.");
      return;
    }

    this._aplicar(valor);
  }

  /** Entero ≥ 1 escrito en el input, o null si no es una cantidad válida. */
  private _parseInput(): number | null {
    const texto = this._input?.value.trim() ?? "";
    if (texto === "") return null;

    const valor = Number(texto);
    if (!Number.isInteger(valor) || valor < 1) return null;

    return valor;
  }

  /** Pinta el valor de inmediato (optimista) y dispara el guardado. Un valor ya
   *  confirmado no vuelve a guardarse, salvo que el último intento haya
   *  fallado. */
  private _aplicar(valor: number): void {
    if (valor === this._valorConfirmado && this._ultimoGuardadoOk) return;

    this._valor = valor;
    this._pintarValor();
    this._guardar();
  }

  /** Guarda el valor actual. Si falla, revierte al último confirmado. */
  private _guardar(): void {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    const request = fx_createFincaArbolesRequest(finca);
    request.arbolesMonitorear = this._valor;

    const peticion = ++this._peticion;

    this._saveService
      .saveAsync(request)
      .then((fincaGuardada) => {
        if (this._disposed || peticion !== this._peticion) return;

        this._valorConfirmado = fincaGuardada.arbolesMonitorear;
        this._ultimoGuardadoOk = true;
        // La finca activa lleva `arbolesMonitorear` adentro: sin esto, el resto
        // de la app seguiría leyendo el valor viejo.
        serviceProvider.getFincaSelectedService().set(fincaGuardada);
        serviceProvider
          .getErrorService()
          .addSuccess(
            TOAST_TITULO,
            `Guardado: ${this._valorConfirmado} árboles por sesión.`,
          );
      })
      .catch((error) => {
        if (this._disposed || peticion !== this._peticion) return;

        this._valor = this._valorConfirmado;
        this._ultimoGuardadoOk = false;
        this._pintarValor();
        serviceProvider
          .getErrorService()
          .addError("Error al guardar los árboles a monitorear", error);
      });
  }

  /** Refleja `_valor` en la stat tile, los presets y el input. */
  private _pintarValor(): void {
    if (this._disposed) return;

    if (this._display) this._display.textContent = String(this._valor);

    this._presets
      ?.querySelectorAll<HTMLButtonElement>("button[data-valor]")
      .forEach((boton) => {
        const activo = Number(boton.dataset.valor) === this._valor;
        boton.classList.toggle("sx-btn-primary", activo);
        boton.classList.toggle("sx-btn-ghost", !activo);
      });

    // Solo se reescribe si difiere (revertir tras un error): escribirlo mientras
    // el usuario tipea le movería el cursor.
    if (this._input && this._input.value !== String(this._valor)) {
      this._input.value = String(this._valor);
    }
  }
}
